import React from 'react';
import { useLocation } from 'react-router-dom';
import { DateType } from '../../Analytics/types';
import { AdsQueryVariables } from './useAdsAnalyticsParams';

interface UseUrlInitializationProps {
  clientId?: string;
  clientCreatedDate?: string;
  defaultDateType: DateType;
  defaultShowNonAttributedData: boolean;
  defaultAdAccountIds?: string[];
  defaultAdCampaignAndSetIds?: string[];
  isFetchingAdAccounts?: boolean;
  updateUrlParams: (params: AdsQueryVariables) => void;
}

/**
 * Hook to initialize URL parameters with default values when appropriate.
 *
 * IMPORTANT: This hook is the SINGLE SOURCE OF TRUTH for determining when to apply defaults.
 * It only applies defaults when the URL has NO query parameters at all.
 *
 * Business Logic:
 * - Clean URL (/ads) → Apply all defaults and update URL
 * - URL with any params (/ads?anything=value) → Do nothing, respect existing state
 *
 * This prevents race conditions and ensures consistent behavior across the application.
 */
export const useUrlInitialization = ({
  clientId,
  clientCreatedDate,
  defaultDateType,
  defaultShowNonAttributedData,
  defaultAdAccountIds,
  defaultAdCampaignAndSetIds,
  isFetchingAdAccounts,
  updateUrlParams,
}: UseUrlInitializationProps) => {
  const location = useLocation();

  // Helper function to check if defaults should be applied
  // CRITICAL: Only apply defaults when NO query parameters exist at all
  const shouldInitializeDefaults = React.useCallback(() => {
    // Check if ANY query params exist at all
    const hasAnyQueryParams = location.search.length > 0;

    // Also check if we have basic required conditions
    const hasRequiredConditions =
      clientId &&
      clientCreatedDate &&
      !isFetchingAdAccounts &&
      defaultAdAccountIds &&
      defaultAdAccountIds.length > 0;

    // Initialize defaults ONLY if no query params exist AND we have required conditions
    return !hasAnyQueryParams && hasRequiredConditions;
  }, [location.search, clientId, clientCreatedDate, isFetchingAdAccounts, defaultAdAccountIds]);

  // Initialize URL params if relevant filter params don't exist
  React.useEffect(() => {
    if (shouldInitializeDefaults()) {
      const initialVariables: AdsQueryVariables = {
        clientId,
        dateType: defaultDateType,
        startDate: '', // Will be calculated by the params hook
        endDate: '', // Will be calculated by the params hook
        showNonAttributedData: defaultShowNonAttributedData,
        fromOldData: true,
        adAccountIds: defaultAdAccountIds,
        adCampaignAndSetIds: defaultAdCampaignAndSetIds || [],
        adObjectiveIds: [],
        memberIds: [],
        compareWithPartnershipAds: false,
      };

      updateUrlParams(initialVariables);
    }
  }, [shouldInitializeDefaults, updateUrlParams, clientId, defaultDateType, defaultShowNonAttributedData, defaultAdAccountIds, defaultAdCampaignAndSetIds, location.search, clientCreatedDate, isFetchingAdAccounts]);
};
